import React from "react";
import { LegalShell, Section, ResponsiveTable } from "../components/LegalShell";

const LAST_UPDATED = "26 February 2026";

export default function Transparency() {
    return (
        <LegalShell
            eyebrow="Transparency"
            title="GDPR & AI Act transparency"
            subtitle="Controller / processor roles, a Records-of-Processing summary, AI Act Article 50 transparency to users, and the model card for the AI system that powers EuroLaw Hub."
            lastUpdated={LAST_UPDATED}
        >
            <Section title="1. Controller / processor roles">
                <ResponsiveTable
                    testId="transparency-roles"
                    columns={[
                        { key: "scenario", label: "Scenario" },
                        { key: "role", label: "Our role" },
                        { key: "notes", label: "Notes" },
                    ]}
                    rows={[
                        { scenario: "Account, billing, support, security telemetry", role: "Controller", notes: "We determine the purposes and means. See Privacy Policy §§ 3–4." },
                        { scenario: "Documents you upload + conversations you submit", role: "Processor (on your instructions)", notes: "We process them solely to deliver the Service to you. The Anthropic DPA prohibits training on customer content." },
                        { scenario: "Watchdog alerts and AI-generated bulletins", role: "Controller for the editorial layer, processor for inputs you submit", notes: "Watch parameters you create are processed on your instructions; the alerts produced are stored in your account." },
                    ]}
                />
            </Section>

            <Section title="2. Records of Processing Activities: summary (Art. 30 GDPR)">
                <ResponsiveTable
                    testId="transparency-ropa"
                    columns={[
                        { key: "activity", label: "Processing activity" },
                        { key: "categories", label: "Categories of data" },
                        { key: "basis", label: "Legal basis" },
                        { key: "recipients", label: "Recipients / sub-processors" },
                        { key: "retention", label: "Retention" },
                    ]}
                    rows={[
                        { activity: "User account management", categories: "Email, hashed password, plan, role", basis: "Art. 6(1)(b)", recipients: "Managed MongoDB hosting (EU)", retention: "Life of account + 90 days" },
                        { activity: "AI-assisted research and uploads", categories: "Prompts, uploaded documents, vectors", basis: "Art. 6(1)(b)", recipients: "Anthropic (DPA + SCCs)", retention: "Until deletion or 24 months of inactivity" },
                        { activity: "Watchdog scans + alerts", categories: "Watch parameters, alert payloads, citations", basis: "Art. 6(1)(b)", recipients: "Anthropic (DPA + SCCs)", retention: "Until dismissed; soft-archive after 6 months" },
                        { activity: "Bulletins (editorial)", categories: "Editorial inputs and outputs", basis: "Art. 6(1)(b) and Art. 6(1)(f) for editorial library", recipients: "Anthropic (DPA + SCCs)", retention: "Indefinite while published; admin can unpublish or delete" },
                        { activity: "Billing", categories: "Plan, subscription IDs, invoice metadata", basis: "Art. 6(1)(b), Art. 6(1)(c)", recipients: "Payment processor / merchant of record (DPA + SCCs)", retention: "Up to 10 years for tax law" },
                        { activity: "Security, audit, rate limiting", categories: "IP, user agent, route, timestamp", basis: "Art. 6(1)(f)", recipients: "Internal infrastructure provider", retention: "12 months" },
                        { activity: "Consent records", categories: "Consent decisions, version, timestamp, IP, UA", basis: "Art. 6(1)(c) + Art. 7(1) accountability", recipients: "Internal database (EU)", retention: "5 years" },
                    ]}
                />
            </Section>

            <Section title="3. AI Act transparency to users (Art. 50)">
                <p>
                    EuroLaw Hub is an AI system within the meaning of Regulation
                    (EU) 2024/1689. We are required to inform users that they are
                    interacting with an AI system; we do so prominently in the
                    landing-page widget, in the Research Assistant interface, in
                    the support chatbot greeting, and across every AI-generated
                    bulletin (which is labelled as such).
                </p>
                <p>
                    Where EuroLaw Hub uses live retrieval to generate content (e.g.
                    Watchdog scans, Bulletin generation), the output is labelled
                    and presented alongside primary-source citations. Human
                    oversight is required for any decision with legal or similarly
                    significant effects. EuroLaw Hub does not make solely-automated
                    decisions about individuals (Art. 22 GDPR).
                </p>
                <p>
                    The product is decision support for qualified legal
                    professionals. It is not intended to replace legal advice, and
                    its outputs are not legal opinions. Users remain responsible
                    for verifying the output against the cited sources and
                    applying it under their own professional judgement.
                </p>
            </Section>

            <Section title="4. Model card: the AI system that powers EuroLaw Hub">
                <ResponsiveTable
                    testId="transparency-model-card"
                    columns={[
                        { key: "field", label: "Field" },
                        { key: "value", label: "Value" },
                    ]}
                    rows={[
                        { field: "Provider", value: "Anthropic, PBC (US). Accessed over the Anthropic API." },
                        { field: "Model family", value: "Claude (Sonnet / light + heavy variants for different module endpoints). EuroLaw Hub never sends prompts to other LLM providers without disclosing the change here." },
                        { field: "Intended use", value: "EU legal research and compliance decision-support for qualified counsel, in-house lawyers and compliance officers. Editorial bulletin drafting. Conversational support for product questions." },
                        { field: "Prohibited uses", value: "See Acceptable Use Policy in the Legal page. Specifically: no solely-automated significant decisions about individuals (Art. 22 GDPR); no Art. 5 AI Act prohibited practices; no generation of unlawful content; no extraction of system prompts." },
                        { field: "Known limitations", value: "Knowledge cut-off applies between training updates. Some answers may be incomplete, outdated or inaccurate. Citations are best-effort and may not include a stable URL. The model is not a fact-checker for the documents you upload. Verify against the source." },
                        { field: "Training data on customer content", value: "Anthropic does NOT train its models on EuroLaw Hub customer content per the DPA between Anthropic and EuroLaw Hub." },
                        { field: "Risk classification under the AI Act", value: "EuroLaw Hub is not a 'high-risk' AI system within the meaning of Annex III. It is a general-purpose research and decision-support tool with mandatory human-in-the-loop and the Art. 50 transparency obligations addressed in §3." },
                        { field: "Human oversight", value: "Every output is reviewed by the qualified user before it is acted upon in a legal matter. EuroLaw Hub does not exercise legal judgement on behalf of users." },
                    ]}
                />
            </Section>

            <Section title="5. Contact for data protection and AI matters">
                <p>
                    Data Protection Officer and AI compliance contact:{" "}
                    <a className="underline" href="mailto:altay@eurolawhub.com">
                        altay@eurolawhub.com
                    </a>
                    . You can also reach us through the support chatbot at the
                    bottom-right of every page (note: the chatbot does not give
                    legal advice and escalates account or data-protection issues
                    to the email above).
                </p>
            </Section>
        </LegalShell>
    );
}
